import * as React from "react";
import { useState, useEffect } from "react";
import moment from "moment";
import TableCell from "@mui/material/TableCell";
import Checkbox from "@mui/material/Checkbox";
import {
  IAdminTransaction,
  IAPIResponse,
  IBuyerProperties,
  IFilterFetch,
  IListings,
} from "../../../../common/utils/interfaces";
import CustomTable, {
  HeadCell,
} from "../../../../common/components/CustomTable/CustomTable";
import CustomBadge from "../../../../common/components/CustomBadge";
import {
  approvePaymentTransaction,
  getAdminBuyerTransactions,
} from "../../../../../apis/admin/purchases";
import { Link } from "react-router-dom";
import { getReceipt } from "../../../../../apis/listings/general";
import { downloadReceipt, formatDate } from "../../../../common/utils/helpers";
import { Button, Menu, MenuItem, Modal, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../store/store";
import { MoreHoriz } from "@mui/icons-material";
import { CustomModalProps } from "../../../../../types";
import { adminGetTransactionDetails } from "../../../../../apis/admin/transaction";

type Props = {
  setDetailData: (arg: IListings) => void;
};

const headCells: readonly HeadCell[] = [
  {
    id: "created",
    numeric: false,
    disablePadding: true,
    label: "Transaction Date",
  },
  {
    id: "buyer_name",
    numeric: false,
    disablePadding: false,
    label: "Buyer Name",
  },
  {
    id: "payment_type_name",
    numeric: false,
    disablePadding: false,
    label: "Payment Type",
  },
  {
    id: "listing_name",
    numeric: false,
    disablePadding: false,
    label: "Property Name",
  },
  {
    id: "payment_status",
    numeric: false,
    disablePadding: false,
    label: "Payment Status",
  },
  {
    id: "actions",
    numeric: false,
    disablePadding: false,
    label: "Actions",
    align: "center",
  },
];

const TransactionsTable: React.FC<Props> = ({ setDetailData }: Props) => {
  const { layoutSearchText, layoutStatusFilter } = useSelector(
    (state: RootState) => state.search
  );
  const [anchorEl, setAnchorEl] = React.useState<
    (EventTarget & SVGSVGElement) | null
  >();
  const [tableData, setTableData] = React.useState<IAdminTransaction[]>([]);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] =
    useState<IAdminTransaction>();
  const tableTitle = { mainText: "My Transactions", subText: "transactions" };

  useEffect(() => {
    fetchTableData();
  }, [layoutSearchText, layoutStatusFilter]);

  const fetchTableData = async () => {
    const response: IAPIResponse | undefined = await getAdminBuyerTransactions({
      filter: layoutStatusFilter,
      search: layoutSearchText,
    });

    if (response?.status) {
      const { data, count } = response;

      //  convert structure to flatter (cos of listing name)
      const restructuredData = data.map((dataUnit: IAdminTransaction) => {
        dataUnit.listing_name = dataUnit.listing.name;
        return dataUnit;
      });
      setTableData(restructuredData);
    }
  };

  const getBadgeMode = (text: string) => {
    switch (text) {
      case "approved":
        return "success";

      case "rejected":
        return "error";

      default:
        return "info";
    }
  };

  const selectData = (
    evt: React.MouseEvent<HTMLSpanElement, MouseEvent>,
    selectedData: IListings
  ) => {
    evt.stopPropagation();

    setDetailData(selectedData);
  };

  const MappedTableRows = ({
    row,
    labelId,
    isItemSelected,
  }: {
    row: IAdminTransaction;
    labelId: string;
    isItemSelected: boolean;
  }) => (
    <>
      <TableCell padding="checkbox">
        <Checkbox
          color="primary"
          checked={isItemSelected}
          inputProps={{
            "aria-labelledby": labelId,
          }}
        />
      </TableCell>
      <TableCell component="th" id={labelId} scope="row" padding="none">
        <div>
          <div>{moment(row.created).format("Do MMM, YYYY")}</div>
          <div
            style={{
              color: "#888",
              fontSize: 12,
            }}
          >
            {moment(row.created).format("hh:mm A")}
          </div>
        </div>
      </TableCell>
      <TableCell align="left">{row.buyer_name}</TableCell>
      <TableCell align="left">{row.payment_type_name}</TableCell>
      <TableCell align="left">{row.listing?.name}</TableCell>
      <TableCell align="left">
        <CustomBadge mode={getBadgeMode(row.payment_status)}>
          {row.payment_status}
        </CustomBadge>
      </TableCell>
      <TableCell align="center">
        {/* {row.download_receipt_url ? (
          <Button
            variant="text"
            style={{
              padding: 4,
              minWidth: "unset",
              fontSize: 12,
              marginLeft: "auto",
              cursor: "pointer",
            }}
            onClick={(evt) => {
              evt.preventDefault();
              evt.stopPropagation();
              downloadReceipt(row.download_receipt_url);
            }}
          >
            Download receipt
          </Button>
        ) : (
          <span
            style={{
              cursor: "default",
              fontSize: 12,
              opacity: 0.4,
              textTransform: "uppercase",
            }}
          >
            Download receipt
          </span>
        )} */}
        <MoreHoriz
          onClick={(evt) => {
            evt.stopPropagation();
            setSelectedTransaction(row);
            setAnchorEl(evt.currentTarget);
          }}
        />
      </TableCell>
    </>
  );

  interface ActionsMenuProp {
    openApproveModal: () => void;
  }
  const ActionsMenu: React.FC<ActionsMenuProp> = ({ openApproveModal }) => {
    return (
      <Menu
        // setAnchorEl={setAnchorEl}
        anchorEl={anchorEl}
        open={!!anchorEl}
        // id={id}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "center", horizontal: "right" }}
        // verifiedPerms={verifiedPerms}
        // userID={userID}
        // setShowSideBar={setShowSideBar}
      >
        <MenuItem
          onClick={() => {
            openApproveModal();
            setAnchorEl(null);
          }}
        >
          Review Payment
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
          }}
        >
          Download Receipt
        </MenuItem>
      </Menu>
    );
  };

  return (
    <>
      {showApprovalModal && (
        <ApproveModal
          transaction={selectedTransaction!}
          handleClose={() => setShowApprovalModal(false)}
          open={showApprovalModal}
        />
      )}
      <CustomTable
        tableTitle={tableTitle}
        headCells={headCells}
        rows={tableData}
        // rows={testt}
        mappedTableRows={MappedTableRows}
      />
      <ActionsMenu openApproveModal={() => setShowApprovalModal(true)} />
    </>
  );
};

export default TransactionsTable;

interface ApprovalModalProps extends CustomModalProps {
  transaction: IAdminTransaction;
}
const ApproveModal: React.FC<ApprovalModalProps> = ({
  handleClose,
  open,
  transaction,
}) => {
  const approveOrReject = async (action: "approve" | "reject", id: string) => {
    const result = await approvePaymentTransaction(action, id);
  };
  const [transactionDetails, setTransactionDetails] =
    useState<IAdminTransaction>();
  useEffect(() => {
    const fetchTransactionDetails = async () => {
      const response = await adminGetTransactionDetails(
        transaction.reference_id
      );
      setTransactionDetails(response);
    };
    fetchTransactionDetails();
  }, []);

  return (
    <Modal onClose={handleClose} open={open}>
      <div className="absolute p-6 max-h-[85vh] max-w-[80vw] overflow-auto space-y-10 bg-white top-[50%] translate-y-[-50%] left-[50%] translate-x-[-50%] w-[90%] sm:w-[1124px] outline-none">
        <div className="info-card !px-6 sm:!px-10">
          <div className="info-card-header grid sm:grid-cols-2">
            <Typography variant="h5">Property Information</Typography>
            <Typography className="hidden sm:block" variant="h5">
              Buyer Information
            </Typography>
          </div>
          <div className="grid gap-y-6 sm:grid-cols-2">
            <div className="grid h-fit gap-4 sm:gap-7">
              <div className="_section">
                <div className="_title">
                  <Typography>Property Name</Typography>
                </div>
                <div className="_data">
                  <Typography style={{ textTransform: "capitalize" }}>
                    {transaction?.listing.name}
                  </Typography>
                </div>
              </div>
              <div className="_section">
                <div className="_title">
                  <Typography>Address</Typography>
                </div>
                <div className="_data">
                  <Typography style={{ textTransform: "capitalize" }}>
                    {transactionDetails?.listing.full_address}
                  </Typography>
                </div>
              </div>
              <div className="_section">
                <div className="_title">
                  <Typography>Price</Typography>
                </div>
                <div className="_data">
                  <Typography style={{ textTransform: "capitalize" }}>
                    {transactionDetails?.listing.price}
                  </Typography>
                </div>
              </div>
            </div>
            <div className="grid gap-4 sm:gap-y-7">
              <div className="info-card-header sm:hidden mt-6">
                <Typography variant="h5">Buyer Information</Typography>
              </div>
              <div className="_section">
                <div className="_title">
                  <Typography>Name</Typography>
                </div>
                <div className="_data">
                  <Typography style={{ textTransform: "capitalize" }}>
                    {transaction?.buyer_name}
                  </Typography>
                </div>
              </div>
              <div className="_section">
                <div className="_title">
                  <Typography>Email</Typography>
                </div>
                <div className="_data">
                  <Typography style={{ textTransform: "capitalize" }}>
                    {transactionDetails?.buyer.email}
                  </Typography>
                </div>
              </div>
              <div className="_section">
                <div className="_title">
                  <Typography>Phone</Typography>
                </div>
                <div className="_data">
                  <Typography style={{ textTransform: "capitalize" }}>
                    {transactionDetails?.buyer.phone_number}
                  </Typography>
                </div>
              </div>
              <div className="_section">
                <div className="_title">
                  <Typography>Payment Method</Typography>
                </div>
                <div className="_data">
                  <Typography style={{ textTransform: "capitalize" }}>
                    {transactionDetails?.payment_method_name}
                  </Typography>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end space-x-4">
          <button
            onClick={() => approveOrReject("reject", transaction.reference_id)}
            className="bg-red-600 text-white border-primary rounded-2xl py-[10px] px-[30px] font-semibold hover:opacity-70"
          >
            REJECT
          </button>
          <button
            onClick={() => approveOrReject("approve", transaction.reference_id)}
            className="bg-primary rounded-lg py-[10px] px-[30px] text-[#F8F8F8] font-semibold hover:opacity-70"
          >
            APPROVE
          </button>
        </div>
      </div>
    </Modal>
  );
};
